import { useCallback, useState } from 'react';
import {
  BudgetDetail,
  BudgetLinkedReceivable,
  fetchBudgetDetailForReceivable,
  isBudgetTransaction,
} from '../utils/budgetReceivable';

export function useBudgetReceivableExpand() {
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [budgetDetailsByAccountId, setBudgetDetailsByAccountId] = useState<Record<string, BudgetDetail>>({});
  const [budgetLoadingAccountId, setBudgetLoadingAccountId] = useState<string | null>(null);

  const loadBudgetDetailForAccount = useCallback(async (acc: BudgetLinkedReceivable) => {
    let cached: BudgetDetail | undefined;
    setBudgetDetailsByAccountId((prev) => {
      cached = prev[acc.id];
      return prev;
    });
    if (cached) return cached;

    setBudgetLoadingAccountId(acc.id);
    try {
      const budget = await fetchBudgetDetailForReceivable(acc);
      if (budget) {
        setBudgetDetailsByAccountId((prev) => ({ ...prev, [acc.id]: budget }));
      }
      return budget;
    } catch {
      return null;
    } finally {
      setBudgetLoadingAccountId((current) => (current === acc.id ? null : current));
    }
  }, []);

  const preloadBudgetDetailsForAccounts = useCallback(async (accounts: BudgetLinkedReceivable[]) => {
    const budgetAccounts = accounts.filter(isBudgetTransaction);
    await Promise.all(budgetAccounts.map((acc) => loadBudgetDetailForAccount(acc)));
  }, [loadBudgetDetailForAccount]);

  const toggleBudgetExpand = useCallback(async (acc: BudgetLinkedReceivable) => {
    if (!isBudgetTransaction(acc)) return;
    if (expandedAccountId === acc.id) {
      setExpandedAccountId(null);
      return;
    }
    setExpandedAccountId(acc.id);
    await loadBudgetDetailForAccount(acc);
  }, [expandedAccountId, loadBudgetDetailForAccount]);

  return {
    expandedAccountId,
    budgetDetailsByAccountId,
    budgetLoadingAccountId,
    toggleBudgetExpand,
    loadBudgetDetailForAccount,
    preloadBudgetDetailsForAccounts,
    isBudgetTransaction,
  };
}
